import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ErrorIcon from '@mui/icons-material/Error';
import LinkIcon from '@mui/icons-material/Link';
import type { FormField } from '@re/form-engine';
import { Box, Chip, IconButton, InputAdornment, Stack, Tooltip, Typography } from '@mui/material';
import { Fragment, useState } from 'react';
import { TextField } from '../../components/text-field';
import { matches } from '../../lib/search';
import { FIELD_TYPE, MAPS_TO_LABEL } from '../../lib/labels';
import { FieldIcon } from './fieldMeta';
import { isEn, tr } from '../../lib/i18n';

interface Props {
  fields: FormField[];
  selected: string | null;
  issueKeys: Set<string>;
  onSelect: (key: string) => void;
  onReorder: (next: FormField[]) => void;
  onDuplicate: (key: string) => void;
  onDelete: (key: string) => void;
  /** one-line description of the field's conditions (tooltip) */
  describe: (f: FormField) => string;
}

type Filter = 'all' | 'issues' | 'conditional' | 'required';

export function FieldList({ fields, selected, issueKeys, onSelect, onReorder, onDuplicate, onDelete, describe }: Props) {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const from = fields.findIndex((f) => f.key === e.active.id);
    const to = fields.findIndex((f) => f.key === e.over!.id);
    onReorder(arrayMove(fields, from, to));
  };
  const test: Record<Filter, (f: FormField) => boolean> = {
    all: () => true,
    issues: (f) => issueKeys.has(f.key),
    conditional: (f) => !!(f.visibleWhen || f.requiredWhen),
    required: (f) => f.required,
  };
  const count = (k: Filter) => fields.filter(test[k]).length;
  const shown = fields.filter((f) => test[filter](f) && matches(q, f.label.ar, f.label.en, f.key, f.section, FIELD_TYPE[f.type]));
  const filtered = !!q || filter !== 'all';
  const chips: { k: Filter; label: string; color?: 'error' }[] = [
    { k: 'all', label: tr('الكل', 'All') },
    { k: 'issues', label: tr('أخطاء', 'Errors'), color: 'error' },
    { k: 'conditional', label: tr('شرطية', 'Conditional') },
    { k: 'required', label: tr('إلزامية', 'Required') },
  ];
  return (
    <Stack spacing={1} sx={{ minHeight: 0, flex: 1 }}>
      <TextField size="small" placeholder={tr('ابحث بالاسم أو المفتاح…', 'Search by name or key…')} value={q} onChange={(e) => setQ(e.target.value)}
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }} />
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {chips.map((c) => {
          const n = count(c.k);
          if (c.k !== 'all' && n === 0) return null;
          const on = filter === c.k;
          return <Chip key={c.k} size="small" label={`${c.label} ${n}`} color={on ? (c.color ?? 'primary') : c.color === 'error' ? 'error' : 'default'} variant={on ? 'filled' : 'outlined'} onClick={() => setFilter(c.k)} />;
        })}
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', p: 0.25, mx: -0.25 }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={shown.map((f) => f.key)} strategy={verticalListSortingStrategy}>
            <Stack spacing={0.5}>
              {shown.map((f, i) => {
                const prev = shown[i - 1];
                const showSection = !filtered && f.type !== 'section' && (f.section ?? null) !== (prev?.section ?? null) && prev?.type !== 'section';
                return (
                  <Fragment key={f.key}>
                    {showSection && f.section && (
                      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ px: 1, pt: i ? 1 : 0 }} dir="ltr" textAlign={isEn() ? 'left' : 'right'}>
                        {f.section}
                      </Typography>
                    )}
                    <Row field={f} index={fields.indexOf(f) + 1} active={selected === f.key} hasIssue={issueKeys.has(f.key)} onClick={() => onSelect(f.key)}
                      onDuplicate={() => onDuplicate(f.key)} onDelete={() => onDelete(f.key)} rule={f.visibleWhen || f.requiredWhen ? describe(f) : ''} />
                  </Fragment>
                );
              })}
              {!shown.length && <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>{tr('لا توجد حقول مطابقة', 'No matching fields')}</Typography>}
            </Stack>
          </SortableContext>
        </DndContext>
      </Box>
    </Stack>
  );
}

function Row({ field: f, index, active, hasIssue, onClick, onDuplicate, onDelete, rule }: { field: FormField; index: number; active: boolean; hasIssue: boolean; onClick: () => void; onDuplicate: () => void; onDelete: () => void; rule: string }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: f.key });
  const isSection = f.type === 'section';
  const conditional = !!(f.visibleWhen || f.requiredWhen);
  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1, pr: 0.5, minHeight: 52, borderRadius: 2, cursor: 'pointer', position: 'relative',
        border: 1, borderColor: hasIssue ? 'error.main' : active ? 'primary.main' : 'divider',
        bgcolor: active ? 'action.selected' : isSection ? 'action.hover' : 'background.paper',
        boxShadow: isDragging ? 6 : 0, opacity: isDragging ? 0.85 : 1, zIndex: isDragging ? 2 : undefined,
        '&:hover': { borderColor: active ? 'primary.main' : 'text.secondary' },
        '& .row-acts': { display: 'none' },
        '&:hover .row-acts, &:focus-within .row-acts': { display: 'flex' },
        '&:hover .row-icons': { display: 'none' },
        // conditional fields get a colored edge on the start side
        '&::before': conditional ? { content: '""', position: 'absolute', insetBlock: 8, left: -1, /* stylis-rtl flips → renders on the right (start edge) */ width: 3, borderRadius: 2, bgcolor: 'secondary.main' } : undefined,
      }}
    >
      <Box {...attributes} {...listeners} onClick={(e) => e.stopPropagation()} sx={{ cursor: 'grab', display: 'flex', color: 'text.disabled', touchAction: 'none' }} aria-label={tr('سحب لإعادة الترتيب', 'Drag to reorder')}>
        <DragIndicatorIcon fontSize="small" />
      </Box>
      <Box sx={{ width: 30, height: 30, borderRadius: 1.5, display: 'grid', placeItems: 'center', flexShrink: 0, bgcolor: isSection ? 'transparent' : 'action.hover', color: active ? 'primary.main' : 'text.secondary' }}>
        <FieldIcon type={f.type} fontSize="small" />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={isSection ? 700 : 600} noWrap>
          {(isEn() && f.label.en) || f.label.ar || <Box component="em" sx={{ opacity: 0.6 }}>{tr('بدون عنوان', 'Untitled')}</Box>}
          {f.required && !isSection && <Box component="span" sx={{ color: 'error.main' }}> *</Box>}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap component="div">
          {index}. {FIELD_TYPE[f.type]}
          {f.mapsTo ? ` ${isEn() ? '→' : '←'} ${MAPS_TO_LABEL[f.mapsTo]}` : ''}
        </Typography>
      </Box>
      <Stack className="row-acts" direction="row" sx={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
        {f.type !== 'request_type' && <Tooltip title={tr('تكرار', 'Duplicate')}><IconButton size="small" onClick={onDuplicate}><ContentCopyIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
        <Tooltip title={tr('حذف', 'Delete')}><IconButton size="small" onClick={onDelete}><DeleteOutlineIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
      </Stack>
      <Stack className="row-icons" direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
        {hasIssue && <Tooltip title={tr('يحتوي أخطاء', 'Has errors')}><ErrorIcon color="error" sx={{ fontSize: 18 }} /></Tooltip>}
        {conditional && <Tooltip title={rule || tr('يظهر أو يُلزم بشرط', 'Conditionally shown or required')}><CallSplitIcon color="secondary" sx={{ fontSize: 18 }} /></Tooltip>}
        {f.mapsTo && <Tooltip title={tr('حقل نظامي — يُحفظ في عمود مفهرس', 'System field — stored in an indexed column')}><LinkIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></Tooltip>}
      </Stack>
    </Box>
  );
}
